// ── 흐름 '기타 금액' 입력/목록 (들어오는 돈 / 나가는 돈 카드 하단 내장) ─────────
// direction='inflow'|'outflow'. 한 항목 = 날짜·적요·금액(±). 색상은 전역 CSS 토큰.

import React, { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { fmtAmount, todayKstYmd } from './constants';
import { stdBlackBtn, stdOutlineBtn } from '../adminlayout/designStandard';

const initialDraft = () => ({ entry_date: todayKstYmd(), amount: '', note: '' });

export default function ManualEntryPanel({ direction, entries = [], onChanged, showToast }) {
  const [draft, setDraft] = useState(initialDraft());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  function reset() {
    setDraft(initialDraft());
    setEditingId(null);
  }

  async function submit() {
    if (!draft.entry_date || draft.amount === '' || draft.amount === '-') {
      showToast?.('일자와 금액을 입력해 주세요.', 'error');
      return;
    }
    setSaving(true);
    const body = {
      direction,
      entryDate: draft.entry_date,
      amount: Number(draft.amount),
      note: draft.note || null,
    };
    const res = editingId
      ? await apiClient.put(`/api/admin/cashflow/manual-entries/${editingId}`, body)
      : await apiClient.post('/api/admin/cashflow/manual-entries', body);
    setSaving(false);
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    showToast?.(editingId ? '수정했습니다.' : '추가했습니다.', 'success');
    reset();
    onChanged?.();
  }

  function startEdit(entry) {
    setEditingId(entry.id);
    setDraft({ entry_date: entry.entry_date, amount: String(entry.amount), note: entry.note || '' });
  }

  async function remove(entry) {
    if (!window.confirm(`${entry.entry_date} ${fmtAmount(entry.amount)}원 항목을 삭제할까요?`)) return;
    const res = await apiClient.delete(`/api/admin/cashflow/manual-entries/${entry.id}`);
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '삭제에 실패했습니다.', 'error');
      return;
    }
    showToast?.('삭제했습니다.', 'success');
    onChanged?.();
  }

  return (
    <div style={wrapStyle}>
      <div style={labelRow}>
        기타 {direction === 'inflow' ? '수입' : '지출'} 추가
        <span style={hintStyle}>날짜 · 적요 · 금액(+/-)</span>
      </div>
      <div style={formRow}>
        <input
          type="date"
          value={draft.entry_date}
          onChange={(e) => setDraft({ ...draft, entry_date: e.target.value })}
          style={inputStyle}
        />
        <input
          type="text"
          inputMode="numeric"
          placeholder="금액(±)"
          value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value.replace(/[^0-9-]/g, '') })}
          style={{ ...inputStyle, width: 92 }}
        />
        <input
          type="text"
          placeholder="적요"
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          style={{ ...inputStyle, flex: 1, minWidth: 60 }}
        />
        <button onClick={submit} disabled={saving} style={primaryButton}>{editingId ? '수정' : '추가'}</button>
        {editingId && <button onClick={reset} disabled={saving} style={secondaryButton}>취소</button>}
      </div>
      {entries.length > 0 && (
        <ul style={listStyle}>
          {entries.map((e) => (
            <li key={e.id} style={rowStyle}>
              <span style={{ width: 70, color: 'var(--text-sub)' }}>{String(e.entry_date || '').slice(5)}</span>
              <span style={{ width: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{fmtAmount(e.amount)}</span>
              <span style={{ flex: 1, color: 'var(--text-sub)', paddingLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.note || ''}</span>
              <button onClick={() => startEdit(e)} style={iconButton}>수정</button>
              <button onClick={() => remove(e)} style={{ ...iconButton, color: 'var(--mm-tone-red-text)' }}>삭제</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const wrapStyle = { marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border)' };
const labelRow = { fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 8, display: 'flex', alignItems: 'baseline', gap: 6 };
const hintStyle = { fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' };
const formRow = { display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' };
const inputStyle = { padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box' };
// 2026-08 디자인 통일: 파랑 primary 제거 — 검정 CTA / 흰 외곽선 (designStandard, 컴팩트 폼이라 패딩만 축소)
const primaryButton = { ...stdBlackBtn, padding: '5px 12px' };
const secondaryButton = { ...stdOutlineBtn, padding: '5px 12px' };
const listStyle = { listStyle: 'none', padding: 0, margin: 0 };
const rowStyle = { display: 'flex', alignItems: 'center', padding: '5px 0', borderTop: '1px solid var(--border)', fontSize: 12 };
const iconButton = { background: 'none', border: 'none', color: 'var(--mm-tone-blue-text)', cursor: 'pointer', padding: '0 5px', fontSize: 12 };
